/**
 * ImageViewer - Modal de visualização de imagens em fullscreen
 * Baseado nos padrões de WhatsApp/Telegram
 *
 * Features:
 * - Navegação com setas do teclado (← →) e botões
 * - Zoom com scroll do mouse (centrado no cursor)
 * - Duplo clique para ampliar/resetar
 * - Arrastar para mover a imagem ampliada
 * - Fechar com ESC
 * - Contador de imagens (X de Y)
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { Icon } from '@/shared/icons';
import { downloadMediaWithFilename } from '../utils/downloadMedia';

interface ImageViewerProps {
  images: Array<{
    id: string;
    url: string;
    caption?: string;
    sentAt: string;
    senderName?: string;
  }>;
  initialIndex: number;
  isOpen: boolean;
  onClose: () => void;
}

interface ViewState {
  zoom: number;
  x: number;
  y: number;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.25;
const DOUBLE_CLICK_ZOOM = 2.5;
// deltaY ~100 por "clique" da roda → fator ~1.16 por clique
const WHEEL_ZOOM_FACTOR = 0.0015;

const INITIAL_VIEW: ViewState = { zoom: 1, x: 0, y: 0 };

// Deriva o nome do arquivo a partir da URL da imagem
const getImageFileName = (url: string): string => {
  try {
    const { pathname } = new URL(url, window.location.origin);
    const name = decodeURIComponent(pathname.substring(pathname.lastIndexOf('/') + 1));
    return name || 'imagem';
  } catch {
    return 'imagem';
  }
};

export const ImageViewer: React.FC<ImageViewerProps> = ({
  images,
  initialIndex,
  isOpen,
  onClose
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [view, setView] = useState<ViewState>(INITIAL_VIEW);
  const [isDragging, setIsDragging] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  // Reset zoom/pan quando mudar de imagem ou reabrir
  useEffect(() => {
    setView(INITIAL_VIEW);
  }, [currentIndex, isOpen]);

  // Atualizar index quando props mudam
  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  }, [images.length]);

  // Mantém a imagem dentro dos limites visíveis (sem "fugir" da tela)
  const clampView = useCallback((zoom: number, x: number, y: number): ViewState => {
    const clampedZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
    const img = imgRef.current;
    const container = containerRef.current;
    if (!img || !container) return { zoom: clampedZoom, x: 0, y: 0 };

    const maxX = Math.max(0, (img.offsetWidth * clampedZoom - container.clientWidth) / 2);
    const maxY = Math.max(0, (img.offsetHeight * clampedZoom - container.clientHeight) / 2);

    return {
      zoom: clampedZoom,
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y))
    };
  }, []);

  // Aplica zoom mantendo o ponto (cx, cy) — relativo ao centro do container — fixo na tela
  const applyZoom = useCallback((getNextZoom: (zoom: number) => number, cx = 0, cy = 0) => {
    setView((prev) => {
      const zoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, getNextZoom(prev.zoom)));
      const ratio = zoom / prev.zoom;
      return clampView(zoom, cx - (cx - prev.x) * ratio, cy - (cy - prev.y) * ratio);
    });
  }, [clampView]);

  const handleZoomIn = useCallback(() => {
    applyZoom((zoom) => zoom + ZOOM_STEP);
  }, [applyZoom]);

  const handleZoomOut = useCallback(() => {
    applyZoom((zoom) => zoom - ZOOM_STEP);
  }, [applyZoom]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        case '+':
        case '=':
          handleZoomIn();
          break;
        case '-':
          handleZoomOut();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, goToPrevious, goToNext, handleZoomIn, handleZoomOut, onClose]);

  // Mouse wheel = zoom (centrado no cursor)
  useEffect(() => {
    if (!isOpen) return;
    const container = containerRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = container.getBoundingClientRect();
      const cx = e.clientX - rect.left - rect.width / 2;
      const cy = e.clientY - rect.top - rect.height / 2;
      applyZoom((zoom) => zoom * Math.exp(-e.deltaY * WHEEL_ZOOM_FACTOR), cx, cy);
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [isOpen, applyZoom]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handlePointerDown = (e: React.PointerEvent<HTMLImageElement>) => {
    if (view.zoom <= 1) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originX: view.x,
      originY: view.y
    };
    setIsDragging(true);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLImageElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    setView((prev) => clampView(
      prev.zoom,
      drag.originX + (e.clientX - drag.startX),
      drag.originY + (e.clientY - drag.startY)
    ));
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLImageElement>) => {
    if (dragRef.current?.pointerId !== e.pointerId) return;
    dragRef.current = null;
    setIsDragging(false);
  };

  const handleDoubleClick = (e: React.MouseEvent<HTMLImageElement>) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const cx = e.clientX - rect.left - rect.width / 2;
    const cy = e.clientY - rect.top - rect.height / 2;
    applyZoom((zoom) => (zoom > 1 ? 1 : DOUBLE_CLICK_ZOOM), cx, cy);
  };

  if (!isOpen || images.length === 0) return null;

  const currentImage = images[currentIndex];

  const handleDownloadImage = async () => {
    if (isDownloading) return;
    try {
      setIsDownloading(true);
      await downloadMediaWithFilename(currentImage.url, getImageFileName(currentImage.url));
    } catch {
      toast.error('Falha ao baixar a imagem');
    } finally {
      setIsDownloading(false);
    }
  };

  // Portal pro body: preso ao container do chat (com overflow/transform), o overlay
  // ficava clipado ao painel. Fullscreen exige containing block = viewport. Ver DS §Overlays.
  return createPortal(
    <div
      ref={containerRef}
      className="fixed inset-0 z-[60] bg-black bg-opacity-95"
      data-testid="image-viewer"
    >
      {/* Área da imagem — ocupa 100% da tela */}
      <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
        <img
          ref={imgRef}
          src={currentImage.url}
          alt={currentImage.caption || 'Imagem'}
          draggable={false}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onDoubleClick={handleDoubleClick}
          className="max-w-full max-h-full object-contain select-none"
          style={{
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.zoom})`,
            transition: isDragging ? 'none' : 'transform 150ms ease-out',
            cursor: view.zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
            touchAction: 'none'
          }}
          data-testid="image-viewer-img"
        />
      </div>

      {/* Header (overlay) */}
      <div className="absolute inset-x-0 top-0 z-10 flex items-center justify-between p-4 text-white bg-gradient-to-b from-black/70 to-transparent pointer-events-none">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{getImageFileName(currentImage.url)}</p>
          <p className="text-xs text-gray-400 truncate">
            {(currentImage.senderName || 'Imagem')} · {new Date(currentImage.sentAt).toLocaleString('pt-BR')}
          </p>
        </div>

        {/* Image Counter */}
        <div className="flex items-center gap-4 pointer-events-auto">
          <span className="text-sm text-gray-300">
            {currentIndex + 1} / {images.length}
          </span>

          {/* Zoom Controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleZoomOut}
              disabled={view.zoom <= MIN_ZOOM}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Zoom out"
              data-testid="image-viewer-zoom-out"
            >
              <Icon name="zoom-out" className="w-5 h-5" />
            </button>
            <span className="text-sm w-12 text-center">{Math.round(view.zoom * 100)}%</span>
            <button
              onClick={handleZoomIn}
              disabled={view.zoom >= MAX_ZOOM}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Zoom in"
              data-testid="image-viewer-zoom-in"
            >
              <Icon name="zoom-in" className="w-5 h-5" />
            </button>
          </div>

          {/* Download Button */}
          <button
            type="button"
            onClick={() => void handleDownloadImage()}
            disabled={isDownloading}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Baixar imagem"
            data-testid="image-viewer-download"
          >
            {isDownloading ? (
              <Icon name="loader" className="w-6 h-6 animate-spin" />
            ) : (
              <Icon name="download" className="w-6 h-6" />
            )}
          </button>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            aria-label="Fechar"
            data-testid="image-viewer-close"
          >
            <Icon name="x" className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Previous Button (overlay) */}
      {images.length > 1 && (
        <button
          onClick={goToPrevious}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          aria-label="Imagem anterior"
          data-testid="image-viewer-prev"
        >
          <Icon name="chevron-left" className="w-8 h-8 text-white" />
        </button>
      )}

      {/* Next Button (overlay) */}
      {images.length > 1 && (
        <button
          onClick={goToNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          aria-label="Próxima imagem"
          data-testid="image-viewer-next"
        >
          <Icon name="chevron-right" className="w-8 h-8 text-white" />
        </button>
      )}

      {/* Caption + Instructions (overlay) */}
      <div className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/70 to-transparent pointer-events-none">
        {currentImage.caption && (
          <div className="p-4 pb-0 text-center">
            <p className="text-white text-sm max-w-2xl mx-auto">{currentImage.caption}</p>
          </div>
        )}
        <div className="p-4 text-center">
          <p className="text-gray-400 text-xs">
            Setas ← → para navegar | Scroll para zoom | Duplo clique para ampliar | Arraste para mover | ESC para fechar
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImageViewer;
